import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Calendar, MapPin, Clock, Users, X, AlertCircle, CheckCircle, Search, RefreshCw, Route, Target, Award, Trophy, Plus } from "lucide-react";
import Navigation from "./Navigation";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useFetch, useApi } from "../hooks/useApi";
import { convertUTCToMeetingTimezone } from "../utils/dateTimeUtils";
import { apiService } from "../services/apiService";
import MeetingCard from "./ui/MeetingCard";
import { getPrimaryImageUrl } from "../utils/imageUtils";
import EventCreationForm from "./EventCreationForm";
import RateClubButton from "./ui/RateClubButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const MyRunsPage = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Remove old state - now using useFetch hook
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showEventForm, setShowEventForm] = useState(false);
  const cardsPerPage = 6;

  // Data fetching is handled by useFetch hooks below

  // Refresh data when user navigates to this page
  // Remove the old useEffect that was causing multiple API calls

  // Use new hooks for data fetching
  const { data: registrations, loading: registrationsLoading, refetch: refetchRegistrations } = useFetch(
    () => apiService.users.getRegistrations(),
    [],
    { enabled: !!user }
  );




  // Use new API hook for canceling registrations
  const { execute: executeCancel, loading: cancelLoading } = useApi();

  const [cancelDialogState, setCancelDialogState] = useState({
    open: false,
    registrationId: null,
    isRecurringWithAutoRegister: false,
  });

  const handleCancelRegistration = async (registrationId) => {
    try {
      // Get the registration to check if it's recurring with auto-register
      const registrations = await apiService.users.getRegistrations();
      const registration = registrations.find(reg => reg.id === registrationId);
      
      if (registration) {
        // Check if this is a recurring meeting with auto-registration enabled
        // We need to get the meeting details to check is_recurring
        // For now, we'll check if auto_register_future is true
        const isRecurring = registration.auto_register_future && registration.parent_meeting_id;
        
        if (isRecurring) {
          // Show dialog with options
          setCancelDialogState({
            open: true,
            registrationId,
            isRecurringWithAutoRegister: true,
          });
        } else {
          // Regular cancellation
          await executeCancel(
            () => apiService.users.cancelRegistration(registrationId, false),
            { 
              showSuccessToast: true,
              successMessage: 'Registration cancelled successfully'
            }
          );
          refetchRegistrations();
        }
      } else {
        // Fallback to regular cancellation if registration not found
        await executeCancel(
          () => apiService.users.cancelRegistration(registrationId, false),
          { 
            showSuccessToast: true,
            successMessage: 'Registration cancelled successfully'
          }
        );
        refetchRegistrations();
      }
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const executeCancelWithOption = async (cancelAllFuture = false) => {
    if (!cancelDialogState.registrationId) return;
    
    try {
      await executeCancel(
        () => apiService.users.cancelRegistration(cancelDialogState.registrationId, cancelAllFuture),
        { 
          showSuccessToast: true,
          successMessage: cancelAllFuture
            ? 'Registration and all future registrations cancelled successfully'
            : 'Registration cancelled successfully'
        }
      );
      setCancelDialogState({ open: false, registrationId: null, isRecurringWithAutoRegister: false });
      refetchRegistrations();
    } catch (error) {
      // Error is already handled by useApi hook
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { color: "bg-green-500/20 text-green-300 border-green-500/30", icon: CheckCircle },
      cancelled: { color: "bg-red-500/20 text-red-300 border-red-500/30", icon: X },
      no_show: { color: "bg-gray-500/20 text-runcards-text-secondary border-gray-500/30", icon: AlertCircle }
    };

    const config = statusConfig[status] || statusConfig.confirmed;
    const IconComponent = config.icon;

    return (
      <Badge className={`${config.color} text-runcards-xs`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  // Helper function to format date in meeting timezone
  const formatDateInMeetingTimezone = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      return meetingDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: meetingTimezone || undefined
      });
    } catch (error) {
      return '';
    }
  };

  // Helper function to get next occurrence of a weekday
  const getNextWeekday = (dayName) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayIndex = days.indexOf(dayName);
    const today = new Date();
    const currentDay = today.getDay();
    
    let daysUntilTarget = (dayIndex - currentDay + 7) % 7;
    if (daysUntilTarget === 0) daysUntilTarget = 7; // Next week if it's the same day
    
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysUntilTarget);
    return targetDate;
  };

  // Helper function to format time to 12-hour format (copied from RunPage)
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    
    // Check if it's already in 12-hour format (contains AM/PM)
    if (time24.includes('AM') || time24.includes('PM')) {
      return time24;
    }
    
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours);
    const mins = minutes || '00';
    
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const displayHours = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    return `${displayHours}:${mins} ${period}`;
  };

  // Helper function to format UTC datetime to meeting timezone (12-hour time)
  const formatScheduledDateToLocalTime = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      const hours = meetingDate.getHours();
      const minutes = meetingDate.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return '';
    }
  };

  // Helper function to parse scheduled date
  const parseScheduledDate = (scheduledDate) => {
    if (!scheduledDate) {
      throw new Error('Missing scheduled date');
    }
    
    // scheduledDate should be a datetime string from the API
    const date = new Date(scheduledDate);
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid scheduled date format');
    }
    
    return date;
  };

  // Categorize runs based on status and meeting date
  const getCategorizedRuns = () => {
    const now = new Date();
    const allRuns = registrations; // Only use registrations data to avoid duplicates
    
    // If no runs or registrations is null/undefined, return empty arrays
    if (!allRuns || allRuns.length === 0) {
      return { upcoming: [], cancelled: [], recent: [] };
    }
    
    // Use date filtering logic for regular users
    const upcoming = allRuns.filter(meeting => {
      if (meeting.status !== 'confirmed') {
        return false;
      }
      
      // Check if required fields exist
      if (!meeting.scheduledDate) {
        return false;
      }
      
      const meetingDate = parseScheduledDate(meeting.scheduledDate);
      return meetingDate > now;
    });
    
    const cancelled = allRuns.filter(meeting => 
      meeting.status === 'cancelled'
    );
    
    const recent = allRuns.filter(meeting => {
      // Include confirmed meetings that have passed their date
      if (meeting.status === 'confirmed') {
        if (!meeting.scheduledDate) {
          return false;
        }
        
        const meetingDate = parseScheduledDate(meeting.scheduledDate);
        return meetingDate <= now;
      }
      
      // Also include meetings that are explicitly marked as completed or no_show
      if (meeting.status === 'completed' || meeting.status === 'no_show') {
        return true;
      }
      
      return false;
    });
    
    
    return { upcoming, cancelled, recent };
  };

  const filterRunsBySearch = (runs) => {
    if (!runs || !searchTerm.trim()) {
      return runs || [];
    }
    
    const searchLower = searchTerm.toLowerCase();
    return runs.filter(meeting => {
      const clubName = meeting.clubName?.toLowerCase() || '';
      const location = meeting.address?.toLowerCase() || '';
      const clubType = meeting.clubType?.toLowerCase() || '';
      const scheduledDate = meeting.scheduledDate?.toLowerCase() || '';
      
      return clubName.includes(searchLower) || 
             location.includes(searchLower) || 
             clubType.includes(searchLower) ||
             scheduledDate.includes(searchLower);
    });
  };

  // Get paginated data for current tab
  const getPaginatedData = (data) => {
    if (!data) return [];
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Get total pages for current tab
  const getTotalPages = (data) => {
    if (!data) return 0;
    return Math.ceil(data.length / cardsPerPage);
  };

  // Reset page when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  // Render meeting card component - using same design as RunPage
  const renderRunCard = (meeting, showCancelButton = true) => {
    // Calculate the actual date for this meeting
    let meetingDate = null;
    let meetingDateString = null;
    let isToday = false;

    if (meeting.scheduledDate) {
      meetingDate = parseScheduledDate(meeting.scheduledDate);
      meetingDateString = formatDateInMeetingTimezone(meeting.scheduledDate, meeting.timezone || null);

      const today = new Date();
      const meetingDateOnly = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
      const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      isToday = meetingDateOnly.getTime() === todayOnly.getTime();
    }

    const isCompletedMeeting = meeting.status === 'confirmed'
      ? (meeting.scheduledDate ? parseScheduledDate(meeting.scheduledDate) <= new Date() : false)
      : (meeting.status === 'completed' || meeting.status === 'no_show');

    return (
      <MeetingCard
        key={meeting.id}
        event={{
          id: meeting.id,
          club_id: meeting.club_id,
          title: meeting.meetingTitle || meeting.clubName,
          description: meeting.what_to_expect || '',
          image: getPrimaryImageUrl(meeting.images, meeting.image || "https://images.unsplash.com/photo-1598012268972-217e6036c419?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85"),
          time: meeting.scheduledDate ? formatScheduledDateToLocalTime(meeting.scheduledDate, meeting.timezone || null) : 'Time TBD',
          attendees: meeting.attendees,
          location: meeting.address,
          date: meetingDateString || 'Scheduled',
          difficulty: meeting.difficulty || meeting.clubType,
          timeUntil: isToday ? 'Today' : meetingDateString || 'Scheduled'
        }}
        onButtonClick={() => {
          if (showCancelButton && meeting.status === 'confirmed') {
            handleCancelRegistration(meeting.id);
          }
        }}
        buttonText={(() => {
          if (showCancelButton && meeting.status === 'confirmed') {
            if (meeting.scheduledDate) {
              const meetingDate = parseScheduledDate(meeting.scheduledDate);
              if (meetingDate <= new Date()) return 'Completed';
            }
            return 'Cancel';
          }
          if (meeting.status === 'confirmed' && meeting.scheduledDate) {
            const meetingDate = parseScheduledDate(meeting.scheduledDate);
            if (meetingDate <= new Date()) return 'Completed';
          }
          return meeting.status.charAt(0).toUpperCase() + meeting.status.slice(1);
        })()}
        showHover={false}
        additionalButtons={(() => {
          
          if (isCompletedMeeting) {
            // Get club ID from the meeting data
            const clubId = meeting.club_id;
            
            if (!clubId) {
              console.error('No club ID found in meeting data:', meeting);
              return null;
            }
            
            return (
              <RateClubButton 
                clubId={clubId} 
                clubName={meeting.clubName}
                onRatingSubmitted={() => {
                  // Optionally refresh data or show success message
                  toast.success('Thank you for your review!');
                }}
              />
            );
          }
          return null;
        })()}
      />
    );
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-runcards-text-secondary">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect handled in useEffect
  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary">
      <style>{`
        button:focus,
        a:focus,
        input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        button:focus-visible,
        a:focus-visible,
        input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <Navigation />
      
      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Marcus_Speed_and_Form.png')",
            filter: "grayscale(100%) brightness(0.6)"
          }}
        />
        <div className="absolute inset-0 bg-runcards-background/30" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto pt-44 sm:pt-52 lg:pt-60">
          {/* Main Title */}
          <div className="mb-6">
            <h2 className="text-runcards-title sm:text-4xl md:text-5xl lg:text-6xl font-runcards-bold mb-4 text-runcards-text-primary w-full" style={{letterSpacing: '-0.02em', transform: 'scaleX(1.5)', whiteSpace: 'nowrap'}}>
              MY RUNS
            </h2>
            <p className="text-runcards-xl sm:text-runcards-xxl font-runcards text-runcards-text-tertiary">
              Manage your running club registrations and upcoming runs.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-runcards-text-secondary w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by club name or run details..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-4 py-4 text-runcards-lg font-runcards bg-runcards-surface border-runcards-border/20 text-runcards-text-primary placeholder:text-runcards-text-secondary focus:bg-runcards-foreground focus:border-runcards-border/40 rounded-l-runcards-lg rounded-r-none"
                  />
                </div>
                <Button
                  onClick={() => {
                    refetchRegistrations();
                  }}
                  className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 px-6 py-4 rounded-r-xl rounded-l-none"
                >
                  <RefreshCw className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Schedule a Run Button for Run Club Users */}
          {user?.user_type === 'runclub' && (
            <div className="mb-8">
              <Button
                onClick={() => setShowEventForm(true)}
                className="bg-white text-black hover:bg-gray-100 px-8 py-4 rounded-xl font-runcards-bold text-runcards-lg transition-all duration-300 font-runcards"
              >
                <Plus className="w-5 h-5 mr-2" />
                Schedule a Run
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-16 px-4 sm:px-6 bg-runcards-background">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => handleTabChange("upcoming")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "upcoming" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
            >
              <Calendar className="w-4 h-4 mr-2 inline" />
              Upcoming
            </button>
            <button
              onClick={() => handleTabChange("cancelled")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "cancelled" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
            >
              <X className="w-4 h-4 mr-2 inline" />
              Cancelled
            </button>
            <button
              onClick={() => handleTabChange("recent")}
              className={`px-4 py-2 rounded-full text-runcards-sm font-runcards transition-all duration-200 ${
                activeTab === "recent" 
                ? 'bg-white text-black'
                : 'bg-runcards-surface text-runcards-text-primary hover:bg-runcards-foreground'
              }`}
            >
              <Clock className="w-4 h-4 mr-2 inline" />
              Recent
            </button>
          </div>

          {/* Content Section */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-12 gap-4">
            <div className="text-center sm:text-left">
              <h2 className="text-runcards-xxl sm:text-runcards-title md:text-3xl lg:text-4xl font-runcards-bold text-runcards-text-primary mb-4">
                {activeTab === "upcoming" ? "Upcoming Runs" : 
                 activeTab === "cancelled" ? "Cancelled Runs" : "Recent Runs"}
              </h2>
              <p className="text-runcards-text-secondary text-runcards-lg font-runcards">
                {(() => {
                  const { upcoming, cancelled, recent } = getCategorizedRuns();
                  const currentData = activeTab === "upcoming" ? upcoming : 
                                    activeTab === "cancelled" ? cancelled : recent;
                  const filteredData = filterRunsBySearch(currentData);
                  return `${filteredData.length} ${filteredData.length === 1 ? 'run' : 'runs'} found`;
                })()}
              </p>
            </div>
          </div>
          
          {(() => {
            const { upcoming, cancelled, recent } = getCategorizedRuns();
            const currentData = activeTab === "upcoming" ? upcoming : 
                              activeTab === "cancelled" ? cancelled : recent;
            const filteredData = filterRunsBySearch(currentData);
            const paginatedData = getPaginatedData(filteredData);
            const totalPages = getTotalPages(filteredData);
            
            return (
              <div>
                {registrationsLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-runcards-text-secondary">Loading your runs...</p>
                  </div>
                ) : filteredData.length === 0 ? (
                  <div className="text-center py-16">
                    <Trophy className="w-20 h-20 text-runcards-text-primary/40 mx-auto mb-6" />
                    <h3 className="text-runcards-xxl font-runcards-bold text-runcards-text-primary mb-4 font-runcards">
                      {activeTab === "upcoming" ? "No upcoming runs found" :
                       activeTab === "cancelled" ? "No cancelled runs found" : "No recent runs found"}
                    </h3>
                    <p className="text-runcards-text-primary/60 mb-8 text-runcards-lg font-runcards">
                      {activeTab === "upcoming" ? "You don't have any upcoming runs scheduled." :
                       activeTab === "cancelled" ? "You haven't cancelled any runs yet." : "You don't have any completed runs yet."}
                    </p>
                    {activeTab === "upcoming" && (
                      <button 
                        onClick={() => window.location.href = '/runclubs'}
                        className="bg-white text-black px-8 py-3 rounded-xl font-runcards-bold text-runcards-md hover:bg-gray-100 transition-all duration-300 font-runcards"
                      >
                        Browse Running Clubs
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                      {paginatedData.map((run) => renderRunCard(run, activeTab === "upcoming"))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex justify-center items-center mt-8 space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="text-runcards-text-primary border-runcards-border/20 hover:bg-runcards-surface"
                        >
                          Previous
                        </Button>
                        <span className="text-runcards-text-primary text-runcards-sm px-4">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="text-runcards-text-primary border-runcards-border/20 hover:bg-runcards-surface"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}

        </div>
      </section>

      {/* Event Creation Form Modal */}
      {showEventForm && (
        <div className="fixed inset-0 bg-runcards-background/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-runcards-background border border-runcards-border/20 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <EventCreationForm 
              onClose={() => setShowEventForm(false)}
              onEventCreated={() => {
                setShowEventForm(false);
                refetchRegistrations(); // Refresh the runs data
                toast.success("Run scheduled successfully!");
              }}
            />
          </div>
        </div>
      )}

      {/* Cancel Registration Dialog */}
      <AlertDialog open={cancelDialogState.open} onOpenChange={(open) => setCancelDialogState(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Registration</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelDialogState.isRecurringWithAutoRegister
                ? 'This is a recurring run. Would you like to cancel just this meeting or all future meetings in this series?'
                : 'Are you sure you want to cancel your registration for this run?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            {cancelDialogState.isRecurringWithAutoRegister ? (
              <>
                <AlertDialogAction
                  onClick={() => executeCancelWithOption(false)}
                  className="bg-gray-600 hover:bg-gray-700"
                >
                  Cancel this meeting only
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={() => executeCancelWithOption(true)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Cancel all future meetings
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction
                onClick={() => executeCancelWithOption(false)}
                className="bg-red-600 hover:bg-red-700"
              >
                Yes, Cancel
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MyRunsPage;